import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable, InjectionToken, inject } from '@angular/core';
import { Observable, catchError, map, throwError } from 'rxjs';
import { ApiEnvelope, unwrap } from './api-envelope';
import { ApiError } from './api-error';

/** URL base del BFF (vacío = mismo origen). La provee la app (`app.config.ts`). */
export const API_BASE_URL = new InjectionToken<string>('API_BASE_URL', { providedIn: 'root', factory: () => '' });

export type QueryParams = Record<string, string | number | boolean | null | undefined>;

/**
 * Cliente HTTP único para el BFF. Devuelve **Observables** (cancelables con `switchMap`/`takeUntil`),
 * ya desempaquetados: el llamador recibe `T`, no `{ ok, data }`, y los fallos llegan como `ApiError`.
 *
 * Las cabeceras de seguridad/trazabilidad (Bearer, X-Correlation-Id, Idempotency-Key) las ponen los
 * interceptores de la app: aquí no se toca ningún token.
 */
@Injectable({ providedIn: 'root' })
export class ApiClient {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = inject(API_BASE_URL);

  get<T>(path: string, params?: QueryParams): Observable<T> {
    return this.request(this.http.get<ApiEnvelope<T>>(this.url(path), this.options(params)));
  }

  post<T>(path: string, body?: unknown, params?: QueryParams): Observable<T> {
    return this.request(this.http.post<ApiEnvelope<T>>(this.url(path), body ?? {}, this.options(params)));
  }

  put<T>(path: string, body?: unknown, params?: QueryParams): Observable<T> {
    return this.request(this.http.put<ApiEnvelope<T>>(this.url(path), body ?? {}, this.options(params)));
  }

  patch<T>(path: string, body?: unknown, params?: QueryParams): Observable<T> {
    return this.request(this.http.patch<ApiEnvelope<T>>(this.url(path), body ?? {}, this.options(params)));
  }

  delete<T>(path: string, params?: QueryParams): Observable<T> {
    return this.request(this.http.delete<ApiEnvelope<T>>(this.url(path), this.options(params)));
  }

  /** Subidas multipart (documentos del candidato). El navegador fija el boundary. */
  postForm<T>(path: string, form: FormData): Observable<T> {
    return this.request(this.http.post<ApiEnvelope<T>>(this.url(path), form, this.options()));
  }

  private request<T>(source: Observable<ApiEnvelope<T>>): Observable<T> {
    return source.pipe(
      map((envelope) => unwrap(envelope)),
      catchError((error: unknown) =>
        throwError(() => (error instanceof HttpErrorResponse ? ApiError.fromHttp(error) : error)),
      ),
    );
  }

  private url(path: string): string {
    return `${this.baseUrl.replace(/\/+$/, '')}${path}`;
  }

  /**
   * `withCredentials` se mantiene como en los servicios existentes (sesión por cookie del BFF).
   * Es la ÚNICA línea que cambia cuando el arquitecto defina cookie vs Bearer.
   */
  private options(params?: QueryParams): { params: HttpParams; withCredentials: true } {
    let httpParams = new HttpParams();
    for (const [key, value] of Object.entries(params ?? {})) {
      if (value !== null && value !== undefined && value !== '') httpParams = httpParams.set(key, String(value));
    }
    return { params: httpParams, withCredentials: true };
  }
}
